// Wrapper for calling backend server APIs.
import { getDeploymentConfig } from "util/util";
import { getFirebaseIdTokenAsync } from "internal/firebase";
import Constants from 'expo-constants';
import { undefA, undefR } from "util/stdtypes";

const deploymentConfig = getDeploymentConfig();


// INTERNAL: Most code should be using the Datastore.callServerAsync() method.
// Only use this if you need to call a server function from outside the Datastore context.
// DRAGONS: This function must not log, since it's called from eventlog
export async function callServerApiAsync({datastore=undefA, component, funcname, params=undefR}) {
    if (datastore?.getMockServerCall()) {
        return callMockServerApiAsync({datastore, component, funcname, params});
    }

    let idToken : string | null = null;
    try {
        idToken = await getFirebaseIdTokenAsync();
    } catch (error) {
        console.error('Error in getFirebaseIdTokenAsync', error);
    }
    const expandedParams = { 
        siloKey: datastore?.getSiloKey() || null,
        structureKey: datastore?.getStructureKey() || null, 
        instanceKey: datastore?.getInstanceKey() || null,
        language: datastore?.getLanguage() || 'english',
        clientVersion: Constants.expoConfig?.version || 'unknown',
        url: window.location.href,
        ...params,
    };
    try {
        const apiUrl = makeApiUrl(component, funcname);
        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + (idToken ?? 'none')
            },
            body: JSON.stringify(expandedParams)
        })
        const result = await response.json();
        if (result.success) {
            return result.data;
        } else {
            console.error('Error in server call', component, funcname, params, result.error);
            return null;
        }
    } catch (error: unknown) {
        const errorMessage = (error as Error).message;
        console.error('Error in fetch', component, funcname, params, errorMessage);
    }
}

export async function callMockServerApiAsync({datastore, component, funcname, params}) {
    const mockServerCall = datastore.getMockServerCall();
    if (mockServerCall?.[component]?.[funcname]) {
        return mockServerCall[component][funcname]({datastore, ...params});
    } else {
        throw new Error('No mock server call for ' + component + '/' + funcname);
    }
};


export async function callServerMultipartApiAsync({datastore, component, funcname, params, fileParams={}}) {
    const idToken = await getFirebaseIdTokenAsync();

    const expandedParams = {...params, 
        structureKey: datastore?.getStructureKey() || null, 
        instanceKey: datastore?.getInstanceKey() || null
    };
    try {
        const formData = new FormData();
        Object.keys(expandedParams).forEach(key => {
            formData.append(key, expandedParams[key]);
        })
        Object.keys(fileParams).forEach(key => {
            const {blob, filename} = fileParams[key];
            formData.append(key, blob, filename);
        });

        const apiUrl = makeApiUrl(component, funcname);
        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                'Authorization': 'Bearer ' + (idToken ?? 'none')
            },
            body: formData,
        })
        const result = await response.json();
        if (result.success) {
            return result.data;
        } else {
            console.error('Error in server call', component, funcname, params, result.error);
            return null;
        }
    } catch (error: unknown) {
        const errorMessage = (error as Error).message;
        console.error('Error in fetch', component, funcname, params, errorMessage);
    }
}

export function makeApiUrl(component, funcname) {
    return `${deploymentConfig.host}/api/${component}/${funcname}`;
}



